<script setup lang="ts">
import { titleBar } from '@/composables/menu/title'
import { useI18n } from 'vue-i18n'
import { onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { type GetArticleResponse, getArticleOpen } from '@/composables/article/article'
import { CODE_OK } from '@/apis/code'
import ResponsivePagination from '@/components/base/common/ResponsivePagination.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const pageSize = 100
const requestURL = useRequestURL()

const nuxtApp = useNuxtApp() as any
if (!nuxtApp.payload.data) {
  nuxtApp.payload.data = {}
}
if (nuxtApp.static && !nuxtApp.static.data) {
  nuxtApp.static.data = {}
}

const currentPage = computed({
  get: () => Number(route.query.page) || 1,
  set: (val) => {
    router.replace({ query: { ...route.query, page: String(val) } })
  }
})

const canonicalUrl = computed(() => {
  const url = new URL(`${requestURL.origin}${route.path}`)
  if (route.query.page != null) {
    url.searchParams.set('page', String(route.query.page))
  }
  return url.toString()
})

const pageTitle = computed(() => '文章列表')
const pageDescription = computed(() => '人生一页公开文章列表')

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

onMounted(() => {
  titleBar.value.hideBack = false
  titleBar.value.backable = true
  titleBar.value.transparent = false
  titleBar.value.title = '文章列表'
  titleBar.value.menus = []
})

const openArticleData = useState<GetArticleResponse | null>(`open-artical`, () => null)

const { data: ssrData } = await useAsyncData(
  `open-artical-data`,
  async () => {
    const { data } = await getArticleOpen(currentPage.value, pageSize)
    return data.value
  },
  { watch: [currentPage] }
)

watch(
  () => ssrData.value,
  (value) => {
    if (value && value.code === CODE_OK) {
      openArticleData.value = (value.data as GetArticleResponse) ?? null
    }
  },
  { immediate: true }
)

const currentList = computed(() => openArticleData.value?.articleDTOList ?? [])
const totalItems = computed(() => openArticleData.value?.total ?? 0)
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize)))

const onPageChange = () => {
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<template>
  <BaseLayout min-h-screen bg-gray-50>
    <div class="w-full bg-gray-50 pt-14 md:pt-4 pb-12 px-4 flex flex-col items-center">
      <div class="w-full max-w-3xl flex-1 flex flex-col">
        <!-- 头部：标题 + 我发布的按钮 -->
        <div class="flex items-center justify-between mb-4">
          <div class="text-xl font-bold text-gray-900">文章列表</div>
          <NuxtLink to="/articles/my" class="text-base px-4 underline"> 我发布的 > </NuxtLink>
        </div>

        <div class="flex-1 space-y-4" v-if="currentList.length > 0">
          <div bg-white rounded-xl p-4 v-for="item in currentList" :key="item.id">
            <ArticleItem :item="item" />
          </div>
        </div>
        <div v-else class="text-base text-gray-400 mx-auto my-20">暂无文章</div>

        <div class="pb-4 mt-8 w-full flex justify-center items-center flex-col" v-if="totalPages > 1">
          <ResponsivePagination
            v-model="currentPage"
            :total="totalPages"
            mode="numeric"
            @change="onPageChange"
            public
            seoMode="seo" />
          <div class="text-[14px] text-gray-800 mt-6 font-medium text-center md:text-left">共{{ totalItems }}条</div>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<style scoped></style>
